// École Nid Douillet - Dashboard Page
// Main dashboard for directors and parents

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@store/authStore';
import { parentApi, ParentDashboard } from '@/services/parent.api';
import { notificationsApi, NotificationStats } from '@/services/notifications.api';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import {
  UserGroupIcon,
  CreditCardIcon,
  ChartBarIcon,
  CalendarIcon,
  BellIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

const DashboardPage: React.FC = () => {
  const { t } = useTranslation(['common', 'dashboard']);
  const { user, isDirector, isParent } = useAuth();
  
  // State management
  const [parentDashboard, setParentDashboard] = useState<ParentDashboard | null>(null);
  const [notificationStats, setNotificationStats] = useState<NotificationStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load dashboard data
  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);

        if (isParent()) {
          // Load parent dashboard data
          const [dashboardResponse, notificationsResponse] = await Promise.all([
            parentApi.getDashboard(),
            notificationsApi.getStats()
          ]);

          if (dashboardResponse.success) {
            setParentDashboard(dashboardResponse.data);
          } else {
            setError(dashboardResponse.message || 'Erreur lors du chargement du tableau de bord');
          }

          if (notificationsResponse.success) {
            setNotificationStats(notificationsResponse.data);
          }
        } else if (isDirector()) {
          // Load director dashboard data (existing mock for now)
          const notificationsResponse = await notificationsApi.getStats();
          if (notificationsResponse.success) {
            setNotificationStats(notificationsResponse.data);
          }
        }
      } catch (err) {
        console.error('Dashboard loading error:', err);
        setError('Erreur de connexion au serveur');
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, [isParent, isDirector]);

  // Mock data for director (until director API is integrated)
  const directorStats = {
    totalChildren: 45,
    totalParents: 38,
    monthlyRevenue: 67500, // in centimes
    pendingPayments: 3,
  };

  const formatCurrency = (amount: number) => {
    return (amount / 100).toLocaleString('fr-FR', {
      style: 'currency',
      currency: 'MAD',
    });
  };

  return (
    <div className="space-y-6">
      {/* Welcome header */}
      <div className="bg-white rounded-xl shadow-soft p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900">
              Bonjour, {user?.firstName} !
            </h1>
            <p className="text-neutral-600 mt-1">
              {isDirector() && "Voici un aperçu de votre école aujourd'hui."}
              {isParent() && "Voici les informations concernant vos enfants."}
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-neutral-500">
              {new Date().toLocaleDateString('fr-FR', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </p>
            <p className="text-xs text-neutral-400 mt-1">
              Année académique 2024-2025
            </p>
          </div>
        </div>
      </div>

      {/* Director Dashboard */}
      {isDirector() && (
        <>
          {/* Stats cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-xl shadow-soft p-6">
              <div className="flex items-center">
                <div className="p-3 rounded-lg bg-primary-100">
                  <UserGroupIcon className="h-6 w-6 text-primary-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-neutral-600">
                    Enfants inscrits
                  </p>
                  <p className="text-2xl font-bold text-neutral-900">
                    {directorStats.totalChildren}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-soft p-6">
              <div className="flex items-center">
                <div className="p-3 rounded-lg bg-secondary-100">
                  <UserGroupIcon className="h-6 w-6 text-secondary-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-neutral-600">
                    Familles
                  </p>
                  <p className="text-2xl font-bold text-neutral-900">
                    {directorStats.totalParents}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-soft p-6">
              <div className="flex items-center">
                <div className="p-3 rounded-lg bg-accent-100">
                  <CreditCardIcon className="h-6 w-6 text-accent-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-neutral-600">
                    Revenus du mois
                  </p>
                  <p className="text-2xl font-bold text-neutral-900">
                    {formatCurrency(directorStats.monthlyRevenue)}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-soft p-6">
              <div className="flex items-center">
                <div className="p-3 rounded-lg bg-red-100">
                  <ChartBarIcon className="h-6 w-6 text-red-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-neutral-600">
                    Paiements en attente
                  </p>
                  <p className="text-2xl font-bold text-neutral-900">
                    {directorStats.pendingPayments}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Quick actions */}
          <div className="bg-white rounded-xl shadow-soft p-6">
            <h2 className="text-lg font-semibold text-neutral-900 mb-4">
              Actions rapides
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <button className="btn btn-primary">
                Ajouter un enfant
              </button>
              <button className="btn btn-secondary">
                Enregistrer un paiement
              </button>
              <button className="btn btn-accent">
                Voir les analyses
              </button>
            </div>
          </div>
        </>
      )}

      {/* Parent Dashboard */}
      {isParent() && (
        <>
          {/* Children info */}
          <div className="bg-white rounded-xl shadow-soft p-6">
            <h2 className="text-lg font-semibold text-neutral-900 mb-4">
              Mes enfants
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 border border-neutral-200 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="h-12 w-12 rounded-full bg-primary-100 flex items-center justify-center">
                    <span className="text-primary-700 font-medium">👶</span>
                  </div>
                  <div>
                    <p className="font-medium text-neutral-900">Enfant 1</p>
                    <p className="text-sm text-neutral-600">Grande Section</p>
                    <p className="text-xs text-neutral-500">5 ans</p>
                  </div>
                </div>
              </div>
              {/* Add more children cards as needed */}
            </div>
          </div>

          {/* Payment info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl shadow-soft p-6">
              <h3 className="text-lg font-semibold text-neutral-900 mb-4">
                Prochain paiement
              </h3>
              <div className="flex items-center space-x-3">
                <CalendarIcon className="h-8 w-8 text-orange-500" />
                <div>
                  <p className="font-medium text-neutral-900">
                    15 février 2024
                  </p>
                  <p className="text-sm text-neutral-600">
                    Scolarité + Services de garde
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-soft p-6">
              <h3 className="text-lg font-semibold text-neutral-900 mb-4">
                Dernier paiement
              </h3>
              <div className="flex items-center space-x-3">
                <CreditCardIcon className="h-8 w-8 text-green-500" />
                <div>
                  <p className="font-medium text-neutral-900">
                    15 janvier 2024
                  </p>
                  <p className="text-sm text-neutral-600">
                    1 250,00 MAD
                  </p>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Recent activity */}
      <div className="bg-white rounded-xl shadow-soft p-6">
        <h2 className="text-lg font-semibold text-neutral-900 mb-4">
          Activité récente
        </h2>
        <div className="space-y-3">
          <div className="flex items-center space-x-3 p-3 bg-neutral-50 rounded-lg">
            <div className="h-2 w-2 rounded-full bg-green-500"></div>
            <p className="text-sm text-neutral-700">
              {isDirector() 
                ? "Nouveau paiement reçu de la famille Alami"
                : "Votre paiement de janvier a été confirmé"
              }
            </p>
            <span className="text-xs text-neutral-500 ml-auto">
              Il y a 2 heures
            </span>
          </div>
          
          <div className="flex items-center space-x-3 p-3 bg-neutral-50 rounded-lg">
            <div className="h-2 w-2 rounded-full bg-blue-500"></div>
            <p className="text-sm text-neutral-700">
              {isDirector()
                ? "Nouvel enfant inscrit en Moyenne Section"
                : "Inscription confirmée pour les services de garde"
              }
            </p>
            <span className="text-xs text-neutral-500 ml-auto">
              Hier
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
